import { Bookmark, FileText } from "lucide-react";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { useSelector, useDispatch } from "react-redux";
import { useState, useMemo } from "react";
import { setComments } from "../../store/ApplicationSlice";
import { postNoteWithApplicationId } from "../../api/notesService";

export function NotesTab() {
  const dispatch = useDispatch();
  const accessToken = localStorage.getItem("access_token");
  const refreshToken = localStorage.getItem("refresh_token");
  const comments = useSelector((state) => state.intakeData.comments);
  const selectedApplicationId = useSelector(
    (state) => state.intakeData.selectedAppId
  );
  const username = useSelector((state) => state.intakeData.username || "User");

  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("All");
  const [showModal, setShowModal] = useState(false);
  const [newNoteText, setNewNoteText] = useState("");
  // console.log(12, { comments });

  const sectionTabs = useMemo(() => {
    const normalizeSectionId = (id) =>
      id.includes("document-details") ? "document-details" : id;

    const unique = new Set(
      comments.map((c) => normalizeSectionId(c.section_id))
    );
    return ["All", ...Array.from(unique)];
  }, [comments]);

  // console.log(37, { sectionTabs });
  const filteredComments = comments.filter((comment) => {
    const matchesSearch = comment.text
      .toLowerCase()
      .includes(searchQuery.toLowerCase());

    const isDocumentDetails = comment.section_id.includes("document-details");
    const matchesTab =
      activeTab === "All" ||
      (activeTab === "document-details" && isDocumentDetails) ||
      comment.section_id === activeTab;

    return matchesSearch && matchesTab;
  });

  function formatTabLabel(tab) {
    if (tab === "All") return "All";
    if (tab.includes("document-details")) return "Document Details";
    return tab
      .split(/[\s-_]+/)
      .slice(0, 2)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }

  async function handleSaveNote() {
    const trimmed = newNoteText.trim();
    if (!trimmed) return;

    const newComment = {
      id: crypto.randomUUID(),
      application_id: selectedApplicationId,
      x: 0,
      y: 0,
      page_number: 0,
      section_id: activeTab === "All" ? "notes" : activeTab,
      tab: "Notes",
      text: trimmed,
      user: {
        name: username,
        initials: username.charAt(0).toUpperCase(),
        color: "#1976d2"
      },
      is_resolved: false,
      is_editing: false,
      is_open: false
    };

    dispatch(setComments([newComment, ...comments]));
    const { id: _id, ...commentWithoutId } = newComment;
    await postNoteWithApplicationId(
      commentWithoutId,
      accessToken,
      refreshToken
    );
    setNewNoteText("");
    setShowModal(false);
  }
  console.log(95, { comments });
  return (
    <Card className="p-4 bg-white shadow-md">
      {/* Header */}
      <div className="flex justify-between items-start mb-4">
        <h3 className="text-base font-semibold flex items-center">
          <FileText className="h-4 w-4 mr-2 text-[#FF5533]" />
          Application Notes
        </h3>
        <Button variant="outlined" size="sm" onClick={() => setShowModal(true)}>
          <Bookmark className="h-4 w-4 mr-1" />
          Add Note
        </Button>
      </div>

      {/* Search + Tabs */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <div className="flex-1">
          <Input
            placeholder="Search notes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full"
          />
        </div>

        <div className="flex gap-2 overflow-x-auto">
          {sectionTabs.map((tab) => (
            <Button
              key={tab}
              size="sm"
              variant={activeTab === tab ? "default" : "outline"}
              onClick={() => setActiveTab(tab)}
              className={`whitespace-nowrap rounded-full px-4 py-1 text-sm transition-colors ${
                activeTab === tab
                  ? "bg-[#FF5533] text-white hover:bg-[#e14c2e]"
                  : "border border-gray-300 text-gray-700 hover:bg-gray-100"
              }`}
            >
              {formatTabLabel(tab)}
              {tab === "All" && (
                <span className="ml-1 text-xs text-muted-foreground">
                  ({comments.length})
                </span>
              )}
            </Button>
          ))}
        </div>
      </div>

      {/* Notes list */}
      <div className="space-y-4">
        {filteredComments.map((comment) => {
          const created = new Date(
            comment.created_at || Date.now()
          ).toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "numeric"
          });

          return (
            <div key={comment.id} className="px-4 py-2 border rounded-md">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h4 className="font-medium">{comment.text}</h4>
                  <p className="text-sm text-gray-500">
                    Added by {comment.user.name} • {created}
                  </p>
                </div>
                <Badge variant="secondary">
                  {formatTabLabel(comment.section_id)}
                </Badge>
              </div>
              <p className="text-gray-700 text-sm">
                Tab: <span className="font-medium">{comment.tab}</span>
              </p>

              {comment.section_id.includes("document-details") && (
                <p className="text-gray-700 text-sm">
                  PDF:{" "}
                  <span className="font-medium">
                    {comment.section_id.split("-").slice(3).join("-").trim()}
                  </span>
                </p>
              )}

              {comment.page_number > 0 && (
                <p className="text-gray-700 text-sm">
                  Page number:{" "}
                  <span className="font-medium">{comment.page_number}</span>
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-lg w-[90%] max-w-md p-6 space-y-4">
            <h2 className="text-lg font-semibold">Add Note</h2>
            <textarea
              value={newNoteText}
              onChange={(e) => setNewNoteText(e.target.value)}
              rows={4}
              placeholder="Write your note..."
              className="w-full border rounded-md p-2 focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outlined" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveNote}>Save</Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
